import { SafetyCertificateOutlined } from '@ant-design/icons';
import { AutoComplete, Badge } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { groupBy } from 'lodash';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { stylesheet } from 'typestyle';

import ContainerContent from '@src/components/container-content';
import { ListFixedHeader } from '@src/components/list-fixed-header';
import { Alert, Button, Card, Input, Table } from '@src/controls';
import { isAuthUserSuperUser } from '@src/modules/auth/auth-selectors';
import {
  AccessCapability,
  AccessGroup,
  CheckUserAccessRequest,
  CheckUserAccessResponse,
  checkUserAccess,
  getAccessUserByEmail,
  getAccessUserById,
} from '@src/services/user-access-service';
import { useSelector } from '@src/utils/hooks';

const capabilityStatuses = {
  allowed: { label: 'Allowed', color: 'success' },
  missing: { label: 'Missing access', color: 'warning' },
  unavailable: { label: 'Feature unavailable', color: 'default' },
  unknown: { label: 'Unable to check', color: 'default' },
} as const;

const permissionStatuses = {
  granted: 'Granted',
  not_granted: 'Not granted',
  unknown: 'Unknown permission',
};

type CheckState =
  | { status: 'idle' | 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; response: CheckUserAccessResponse; linkError?: string };

type CapabilityRow = AccessCapability | Pick<AccessCapability, 'id' | 'area'>;

export const UserAccessPage: React.FC = () => {
  const isSuperuser = useSelector(isAuthUserSuperUser);

  return isSuperuser ? (
    <CheckUserAccess />
  ) : (
    <ContainerContent>
      <Alert
        type="error"
        showIcon
        message="Access denied"
        description="Superuser access required."
      />
    </ContainerContent>
  );
};

const CheckUserAccess: React.FC = () => {
  const history = useHistory();
  const [email, setEmail] = React.useState('');
  const [siteId, setSiteId] = React.useState('');
  const [permission, setPermission] = React.useState('');
  const [permissionNames, setPermissionNames] = React.useState<string[]>([]);
  const [check, setCheck] = React.useState<CheckState>({ status: 'idle' });
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const requestVersion = React.useRef(0);

  const linkedUser = React.useRef<{ id: string; email?: string }>();
  const identityVersion = React.useRef(0);
  const writingURL = React.useRef(false);

  const writeURL = React.useCallback(
    (userId: string | undefined, site: string, permissionName: string) => {
      const params = new URLSearchParams();
      if (userId) params.set('userId', userId);
      if (site.trim()) params.set('siteId', site.trim());
      if (permissionName.trim()) params.set('permission', permissionName.trim());
      // Our form writes must not start another URL-driven lookup/check.
      writingURL.current = true;
      history.replace({ search: params.toString() ? `?${params}` : '' });
      writingURL.current = false;
    },
    [history]
  );

  const runCheck = React.useCallback(
    async (input: CheckUserAccessRequest, version: number) => {
      setCheck({ status: 'loading' });
      setExpanded([]);
      const knownUser = linkedUser.current;
      const identity =
        knownUser?.email === input.email
          ? Promise.resolve({ id: knownUser.id, email: input.email })
          : getAccessUserByEmail(input.email);
      let linkError: string | undefined;
      void identity.then(
        user => {
          if (version !== requestVersion.current) return;
          linkedUser.current = user;
          setEmail(user.email);
          writeURL(user.id, input.siteId, input.permission || '');
        },
        () => {
          if (version !== requestVersion.current) return;
          linkError =
            'The access results are still available. Check again to retry creating the link.';
          setCheck(current => (current.status === 'success' ? { ...current, linkError } : current));
        }
      );
      try {
        const response = await checkUserAccess(input);
        if (version !== requestVersion.current) return;
        setCheck({ status: 'success', response, linkError });
        if (!response.error) setPermissionNames(response.permissionNames);
      } catch (error) {
        if (version === requestVersion.current) {
          setCheck({
            status: 'error',
            message: error instanceof Error ? error.message : 'Unable to check user access.',
          });
        }
      }
    },
    [writeURL]
  );

  React.useEffect(() => {
    const restore = async (search: string) => {
      const version = ++requestVersion.current;
      const lookupVersion = ++identityVersion.current;
      const params = new URLSearchParams(search);
      const userId = params.get('userId')?.trim();
      const site = params.get('siteId')?.trim() || '';
      const permissionName = params.get('permission')?.trim() || '';
      linkedUser.current = userId ? { id: userId } : undefined;
      setEmail('');
      setSiteId(site);
      setPermission(permissionName);
      setExpanded([]);
      setCheck({ status: userId ? 'loading' : 'idle' });
      writeURL(userId, site, permissionName);
      if (!userId) return;
      try {
        const user = await getAccessUserById(userId);
        if (lookupVersion !== identityVersion.current) return;
        linkedUser.current = user;
        setEmail(user.email);
        if (version !== requestVersion.current) return;
        if (site) {
          await runCheck(
            {
              email: user.email,
              siteId: site,
              ...(permissionName ? { permission: permissionName } : {}),
            },
            version
          );
        } else {
          setCheck({ status: 'idle' });
        }
      } catch (error) {
        if (lookupVersion === identityVersion.current) {
          setCheck({
            status: 'error',
            message: error instanceof Error ? error.message : 'Unable to resolve user.',
          });
        }
      }
    };
    const pathname = history.location.pathname;
    let search = history.location.search;
    const unlisten = history.listen(location => {
      if (location.pathname !== pathname) {
        requestVersion.current++;
        identityVersion.current++;
        return;
      }
      const searchChanged = location.search !== search;
      search = location.search;
      if (!writingURL.current && searchChanged) void restore(location.search);
    });
    void restore(history.location.search);
    return () => {
      unlisten();
      requestVersion.current++;
      identityVersion.current++;
    };
  }, [history, runCheck, writeURL]);

  const invalidate = () => {
    requestVersion.current++;
    setCheck({ status: 'idle' });
    setExpanded([]);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await runCheck(
      {
        email: email.trim(),
        siteId: siteId.trim(),
        ...(permission.trim() ? { permission: permission.trim() } : {}),
      },
      ++requestVersion.current
    );
  };

  const response = check.status === 'success' ? check.response : undefined;
  const error = check.status === 'error' ? check.message : response?.error?.message;
  const permissionCheck = response?.permissionCheck;
  const capabilities = response && !response.error ? response.capabilities : [];
  const rows: CapabilityRow[] = Object.entries(
    groupBy(capabilities, capability => capability.area)
  ).flatMap(([area, entries]) => [{ id: `area:${area}`, area }, ...entries]);
  const columns: ColumnsType<CapabilityRow> = [
    {
      title: 'Action',
      width: '27%',
      render: (_, row) =>
        'status' in row
          ? row.name
          : { children: row.area, props: { colSpan: 4, className: styles.area } },
    },
    {
      title: 'Result',
      width: '19%',
      render: (_, row) =>
        'status' in row ? (
          <Badge
            status={capabilityStatuses[row.status].color}
            text={capabilityStatuses[row.status].label}
          />
        ) : (
          { props: { colSpan: 0 } }
        ),
    },
    {
      title: 'Why?',
      width: '43%',
      render: (_, row) => ('status' in row ? row.reason : { props: { colSpan: 0 } }),
    },
    {
      width: '11%',
      render: (_, row) =>
        'status' in row ? (
          <Button
            type="link"
            className={styles.explain}
            aria-expanded={expanded.includes(row.id)}
            aria-controls={`access-detail-${row.id}`}
            onClick={() =>
              setExpanded(current =>
                current.includes(row.id)
                  ? current.filter(id => id !== row.id)
                  : [...current, row.id]
              )
            }
          >
            {expanded.includes(row.id) ? 'Hide' : 'Explain'}
          </Button>
        ) : (
          { props: { colSpan: 0 } }
        ),
    },
  ];

  return (
    <ContainerContent className={styles.page}>
      <ListFixedHeader
        title="Check user access"
        IconComponent={SafetyCertificateOutlined}
        showSearch={false}
      />
      <Alert
        type="info"
        showIcon
        className={styles.alert}
        message="Permission changes can take a little time to take effect. Server checks show what is enforced now; group memberships show saved assignments."
      />
      <Card bordered={false} className={styles.formCard}>
        <form onSubmit={submit} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="access-email">User email</label>
            <Input
              id="access-email"
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={event => {
                setEmail(event.target.value);
                linkedUser.current = undefined;
                identityVersion.current++;
                invalidate();
                writeURL(undefined, siteId, permission);
              }}
            />
          </div>
          <div className={styles.siteField}>
            <label htmlFor="access-site">Site ID</label>
            <Input
              id="access-site"
              required
              autoComplete="off"
              value={siteId}
              onChange={event => {
                setSiteId(event.target.value);
                invalidate();
                writeURL(linkedUser.current?.id, event.target.value, permission);
              }}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="access-permission">Permission (optional)</label>
            <AutoComplete
              id="access-permission"
              className={styles.autocomplete}
              value={permission}
              options={permissionNames.map(value => ({ value }))}
              filterOption={(input, option) =>
                String(option?.value).toLowerCase().includes(input.toLowerCase())
              }
              onChange={value => {
                setPermission(value);
                invalidate();
                writeURL(linkedUser.current?.id, siteId, value);
              }}
              placeholder="All capabilities"
              allowClear
            />
          </div>
          <Button
            type="primary"
            htmlType="submit"
            className={styles.submit}
            disabled={!email.trim() || !siteId.trim() || check.status === 'loading'}
            loading={check.status === 'loading'}
          >
            {check.status === 'loading' ? 'Checking…' : 'Check access'}
          </Button>
        </form>
      </Card>
      <div aria-live="polite" aria-busy={check.status === 'loading'}>
        {error && (
          <Alert
            type="error"
            showIcon
            className={styles.alert}
            message="Unable to check user access"
            description={error}
          />
        )}
        {!error && check.status === 'success' && check.linkError && (
          <Alert
            type="warning"
            showIcon
            className={styles.alert}
            message="Unable to create shareable link"
            description={check.linkError}
          />
        )}
        {!error && permissionCheck && (
          <Alert
            type={permissionCheck.status === 'granted' ? 'success' : 'warning'}
            showIcon
            className={styles.alert}
            message={`${permissionStatuses[permissionCheck.status]} — ${permissionCheck.name}`}
            description={permissionCheck.reason}
          />
        )}
        <Card bordered={false}>
          {response && !response.error && (
            <section aria-labelledby="capabilities-heading">
              <h2 id="capabilities-heading" className={styles.heading}>
                {permissionCheck ? 'Related actions' : 'What can this user do?'}
              </h2>
              <Table<CapabilityRow>
                hideTitle
                useAntdEmptyMessage
                locale={{ emptyText: 'No related actions' }}
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={false}
                size="middle"
                tableLayout="fixed"
                scroll={{ x: 700 }}
                expandable={{
                  expandedRowKeys: expanded,
                  expandIconColumnIndex: -1,
                  rowExpandable: row => 'status' in row,
                  expandedRowRender: row =>
                    'status' in row ? (
                      <div id={`access-detail-${row.id}`}>
                        <ul className={styles.evidence}>
                          {row.permissions.map(evidence => (
                            <li key={evidence.name}>
                              <code>{evidence.name}</code> — {permissionStatuses[evidence.status]}
                              {evidence.sources.map(source => (
                                <div
                                  key={`${source.groupId}:${source.scope.type}:${source.scope.id}`}
                                >
                                  {source.groupName} <code>({source.groupId})</code> —{' '}
                                  {source.scope.name || 'Name unavailable'} ({source.scope.type}{' '}
                                  scope: <code>{source.scope.id}</code>)
                                </div>
                              ))}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null,
                }}
              />
            </section>
          )}
          <section className={styles.groups} aria-labelledby="groups-heading">
            <h2 id="groups-heading" className={styles.heading}>
              Groups and memberships
            </h2>
            <Table<AccessGroup>
              hideTitle
              useAntdEmptyMessage
              locale={{ emptyText: 'No groups to display' }}
              rowKey="id"
              dataSource={response && !response.error ? response.groups : []}
              columns={groupColumns}
              pagination={false}
              size="middle"
              tableLayout="fixed"
              scroll={{ x: 700 }}
            />
          </section>
        </Card>
      </div>
    </ContainerContent>
  );
};

const groupColumns: ColumnsType<AccessGroup> = [
  {
    title: 'Group',
    width: '26%',
    render: (_, group) => (
      <>
        {group.name}
        <code className={styles.id}>{group.id}</code>
      </>
    ),
  },
  {
    title: 'Scope',
    width: '32%',
    render: (_, group) => (
      <>
        {group.scope.name || 'Name unavailable'}
        <div className={styles.scope}>
          {group.scope.type === 'site' ? 'Site' : 'Merchant'} scope
        </div>
        <code className={styles.id}>{group.scope.id}</code>
      </>
    ),
  },
  { title: 'Member?', width: '10%', render: (_, group) => (group.member ? 'Yes' : 'No') },
  {
    title: 'Permissions',
    width: '32%',
    render: (_, group) =>
      group.permissions.map(name => (
        <div key={name}>
          <code>{name}</code>
        </div>
      )),
  },
];

const styles = stylesheet({
  page: {
    padding: '24px 28px',
    $nest: {
      '.ant-table': { fontSize: 13 },
      '.ant-table-cell': { overflowWrap: 'anywhere' },
      '.ant-badge-status': { whiteSpace: 'nowrap' },
      '.ant-badge-status-text': { fontSize: 12 },
      '.ant-table-expanded-row td': { background: '#fcfcfc' },
    },
  },
  formCard: { marginBottom: 20 },
  form: { display: 'flex', gap: 18, alignItems: 'flex-start' },
  field: {
    flex: 1,
    minWidth: 0,
    $nest: { label: { display: 'block', marginBottom: 7, color: '#333' } },
  },
  siteField: {
    flex: 1.3,
    minWidth: 0,
    $nest: { label: { display: 'block', marginBottom: 7, color: '#333' } },
  },
  autocomplete: { width: '100%' },
  submit: { marginTop: 28, minWidth: 116 },
  alert: { marginBottom: 20 },
  heading: { fontSize: 15, fontWeight: 600, margin: '0 0 14px' },
  area: { background: '#fff', fontSize: 12, fontWeight: 600, paddingTop: '19px !important' },
  explain: { padding: 0, height: 'auto', textDecoration: 'underline' },
  evidence: { paddingLeft: 18, fontSize: 12, $nest: { li: { marginBottom: 7 } } },
  groups: { marginTop: 28, $nest: { '&:first-child': { marginTop: 0 } } },
  id: { display: 'block', fontSize: 11, color: '#888' },
  scope: { color: '#888', fontSize: 11, marginTop: 2 },
});
